import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  MenuItem,
  Select,
  Typography,
  SelectChangeEvent,
  FormControl,
} from "@mui/material";
import { FileDownload, Print } from "@mui/icons-material";
import axios from "axios";
import { toast } from "react-toastify";
import * as xlsx from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import DynamicTable from "../../../components/lab-dynamic-table";
import { useSelector } from "react-redux";
import DateRangeSelector from "../../../components/DateRangeSelector";

interface LedgerTest {
  testId: string;
  testName: string;
  quantity: number;
}

interface StockLedgerData {
  _id: string;
  createdAt: string;
  assetName: string;
  batchNo: string | null;
  expiryDate?: string;
  movementType: "DEDUCTION" | "SHORTAGE";
  event: string;
  tests?: LedgerTest[];
  testsDeducted: number;
  quantityDeducted: number;
  shortTests?: number;
  testsBalanceBefore?: number;
  testsBalanceAfter?: number;
  statusBefore?: string;
  statusAfter?: string;
  collectionCenterDetails?: { collectionCenterName?: string };
  userDetails?: { fullName?: string; firstName?: string };
}

// The ledger stores the raw configuration value, the tab shows the workflow step it maps to.
const EVENT_LABELS: Record<string, string> = {
  "Deduct stock at sample collection": "Sample Collection",
  "Deduct stock at perform test": "Perform Test",
};

const StockLedger: React.FC = () => {
  const today = new Date().toISOString().split("T")[0];

  const profile = useSelector((state: any) => state.profileSlice.profile);
  const collectionCenters = profile?.data?.collectionCenter || [];

  const [ledgerData, setLedgerData] = useState<StockLedgerData[]>([]);
  const [assets, setAssets] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  const [dateRange, setDateRange] = useState("Today");
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [selectedAssetId, setSelectedAssetId] = useState<string>("all");
  const [selectedCollectionCenterId, setSelectedCollectionCenterId] =
    useState<string>("all");

  const handleCollectionCenterChange = (event: SelectChangeEvent) => {
    setSelectedCollectionCenterId(event.target.value);
    setPage(0);
  };

  const handleAssetChange = (event: SelectChangeEvent) => {
    setSelectedAssetId(event.target.value);
    setPage(0);
  };

  const fetchAssets = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_LAB_URL}/assets/getAllAssets`,
        {
          params: { count: 1000, pageNo: 1 },
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      if (res?.data?.isSuccess) {
        setAssets(res.data?.data || []);
      }
    } catch (error) {
      console.error("Error fetching assets:", error);
    }
  };

  useEffect(() => {
    fetchAssets();
  }, []);

  const fetchStockLedger = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_LAB_URL}/asset-batch/stockLedger`,
        {
          params: {
            count: rowsPerPage,
            pageNo: page + 1,
            fromDate,
            toDate,
            ...(selectedAssetId !== "all" && { assetId: selectedAssetId }),
            ...(selectedCollectionCenterId !== "all" && {
              collectionCenterId: selectedCollectionCenterId,
            }),
          },
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      if (response.data.isSuccess) {
        setLedgerData(response.data.data || []);
        setTotalRecords(
          response.data.totalCount || response.data.data?.length || 0
        );
      } else {
        toast.error(response.data.message || "Failed to fetch stock ledger");
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Error fetching stock ledger data"
      );
      setLedgerData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStockLedger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fromDate,
    toDate,
    page,
    rowsPerPage,
    selectedAssetId,
    selectedCollectionCenterId,
  ]);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const eventLabel = (row: StockLedgerData) =>
    EVENT_LABELS[row.event] || row.event || "-";

  const testNames = (row: StockLedgerData) =>
    (row.tests || []).map(test => test.testName).filter(Boolean).join(", ") ||
    "-";

  const deductedLabel = (row: StockLedgerData) =>
    row.movementType === "SHORTAGE"
      ? `Short ${row.shortTests ?? 0}`
      : `${row.testsDeducted ?? 0}`;

  const balanceLabel = (row: StockLedgerData) =>
    row.movementType === "SHORTAGE"
      ? "-"
      : `${row.testsBalanceBefore ?? 0} → ${row.testsBalanceAfter ?? 0}`;

  const statusLabel = (row: StockLedgerData) =>
    row.movementType === "SHORTAGE"
      ? "-"
      : row.statusBefore === row.statusAfter
        ? row.statusAfter || "-"
        : `${row.statusBefore || "-"} → ${row.statusAfter || "-"}`;

  const handleExcelExport = () => {
    if (!ledgerData || ledgerData.length === 0) {
      toast.warn("No data to export.");
      return;
    }

    const worksheetData = ledgerData.map(row => ({
      "Date & Time": new Date(row.createdAt).toLocaleString(),
      Asset: row.assetName || "-",
      "Batch No": row.batchNo || "-",
      "Collection Center":
        row.collectionCenterDetails?.collectionCenterName || "-",
      Event: eventLabel(row),
      Tests: testNames(row),
      Deducted: deductedLabel(row),
      "Qty Used": row.quantityDeducted ?? 0,
      Balance: balanceLabel(row),
      "Batch Status": statusLabel(row),
      "Done By":
        row.userDetails?.fullName || row.userDetails?.firstName || "-",
    }));

    const worksheet = xlsx.utils.json_to_sheet(worksheetData);
    const workbook = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(workbook, worksheet, "Stock Ledger");
    xlsx.writeFile(workbook, `Asset_Stock_Ledger_${fromDate}_to_${toDate}.xlsx`);
  };

  const handlePrint = () => {
    if (!ledgerData || ledgerData.length === 0) {
      toast.warn("No data to print.");
      return;
    }

    const doc = new jsPDF({ orientation: "landscape" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 8;
    let yPosition = margin;

    doc
      .setFontSize(10)
      .setFont("helvetica", "bold")
      .setTextColor(25, 55, 85)
      .text("ASSET STOCK LEDGER", pageWidth / 2, yPosition, {
        align: "center",
      });

    yPosition += 3;
    doc
      .setDrawColor(25, 55, 85)
      .setLineWidth(0.5)
      .line(margin, yPosition, pageWidth - margin, yPosition);

    yPosition += 3;
    doc
      .setFontSize(6.5)
      .setFont("helvetica", "normal")
      .setTextColor(80, 80, 80)
      .text(
        `Report Period: ${fromDate} to ${toDate} | Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`,
        pageWidth / 2,
        yPosition,
        { align: "center" }
      );

    yPosition += 6;

    const head = [
      [
        "DATE & TIME",
        "ASSET",
        "BATCH NO",
        "CENTER",
        "EVENT",
        "TESTS",
        "DEDUCTED",
        "QTY USED",
        "BALANCE",
        "BATCH STATUS",
        "DONE BY",
      ],
    ];

    const body = ledgerData.map(row => [
      new Date(row.createdAt).toLocaleString(),
      row.assetName || "-",
      row.batchNo || "-",
      row.collectionCenterDetails?.collectionCenterName || "-",
      eventLabel(row),
      testNames(row),
      deductedLabel(row),
      `${row.quantityDeducted ?? 0}`,
      balanceLabel(row),
      statusLabel(row),
      row.userDetails?.fullName || row.userDetails?.firstName || "-",
    ]);

    autoTable(doc, {
      startY: yPosition,
      head,
      body,
      headStyles: {
        fillColor: [25, 55, 85],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 6,
        halign: "center",
        valign: "middle",
        cellPadding: 2,
      },
      bodyStyles: {
        fontSize: 5.5,
        textColor: [40, 40, 40],
        cellPadding: 1.8,
        halign: "center",
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250],
      },
      margin: { top: margin, right: margin, bottom: margin, left: margin },
      didDrawPage: function (data) {
        const pageSize = doc.internal.pageSize;
        const pageCount = doc.getNumberOfPages();
        doc.setFontSize(5);
        doc.setTextColor(120, 120, 120);
        doc.text(
          `Page ${data.pageNumber} of ${pageCount}`,
          pageSize.getWidth() / 2,
          pageSize.getHeight() - 4,
          { align: "center" }
        );
      },
    });

    doc.autoPrint();
    const blob = doc.output("blob");
    const blobUrl = URL.createObjectURL(blob);
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    document.body.appendChild(iframe);

    const cleanup = () => {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(blobUrl);
      }
      window.removeEventListener("focus", cleanup);
    };

    window.addEventListener("focus", cleanup);
    iframe.src = blobUrl;

    iframe.onload = () => {
      try {
        const win = iframe.contentWindow;
        if (win) {
          win.focus();
          win.print();
        }
      } catch (e) {
        console.error("Error triggering print dialog:", e);
        cleanup();
      }
    };
  };

  const headers = [
    "Date & Time",
    "Asset",
    "Batch No",
    "Collection Center",
    "Event",
    "Tests",
    "Deducted",
    "Qty Used",
    "Balance (Before → After)",
    "Batch Status",
    "Done By",
  ];

  const tableData = ledgerData.map(row => {
    const isShortage = row.movementType === "SHORTAGE";
    return [
      new Date(row.createdAt).toLocaleString(),
      row.assetName || "-",
      row.batchNo || "-",
      row.collectionCenterDetails?.collectionCenterName || "-",
      eventLabel(row),
      testNames(row),
      <Box
        component="span"
        sx={{ color: isShortage ? "#dc2626" : "#16a34a", fontWeight: 600 }}
      >
        {deductedLabel(row)}
      </Box>,
      row.quantityDeducted ?? 0,
      balanceLabel(row),
      <Box
        component="span"
        sx={{
          color:
            row.statusAfter === "CONSUMED"
              ? "#dc2626"
              : row.statusAfter === "OPENED"
                ? "#d97706"
                : "inherit",
          fontWeight: 600,
        }}
      >
        {statusLabel(row)}
      </Box>,
      row.userDetails?.fullName || row.userDetails?.firstName || "-",
    ];
  });

  const gradientButtonStyle = {
    minWidth: { xs: "auto", md: "80px" },
    fontSize: { xs: "12px", md: "12px" },
    fontWeight: "600",
    textTransform: "none",
    borderRadius: "8px",
    background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
    color: "#ffffff",
    boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
    transition: "all 0.3s ease-in-out",
    "&:hover": {
      background: "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
      boxShadow: "0 5px 15px rgba(25, 118, 210, .4)",
      transform: "translateY(-1px)",
    },
  };

  return (
    <Box sx={{ background: "#fff", borderRadius: "12px" }}>
      <Box
        sx={{
          p: 1,
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<FileDownload />}
            onClick={handleExcelExport}
            sx={gradientButtonStyle}
          >
            Excel
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<Print />}
            onClick={handlePrint}
            sx={gradientButtonStyle}
          >
            Print
          </Button>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: 2,
            flexWrap: "wrap",
            justifyContent: "flex-end",
            flexDirection: { xs: "column", sm: "row" },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
            <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5 }}>
              Collection Center
            </Typography>
            <FormControl
              className="custom-input"
              size="small"
              fullWidth
              sx={{ minWidth: { xs: "100%", sm: "180px" } }}
            >
              <Select
                value={selectedCollectionCenterId}
                onChange={handleCollectionCenterChange}
                displayEmpty
              >
                <MenuItem value="all">All</MenuItem>
                {collectionCenters.map((center: any) => (
                  <MenuItem key={center._id} value={center._id}>
                    {center.collectionCenterName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
            <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5 }}>
              Asset
            </Typography>
            <FormControl
              className="custom-input"
              size="small"
              fullWidth
              sx={{ minWidth: { xs: "100%", sm: "180px" } }}
            >
              <Select
                value={selectedAssetId}
                onChange={handleAssetChange}
                displayEmpty
              >
                <MenuItem value="all">All</MenuItem>
                {assets.map((asset: any) => (
                  <MenuItem key={asset._id} value={asset._id}>
                    {asset.assetName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <DateRangeSelector
            preset="lab"
            dateRange={dateRange}
            fromDate={fromDate}
            toDate={toDate}
            onDateRangeChange={(value) => {
              setDateRange(value);
              setPage(0);
            }}
            onFromDateChange={setFromDate}
            onToDateChange={setToDate}
          />
        </Box>
      </Box>

      <Box sx={{ p: 1, maxHeight: "60vh", overflowY: "hidden" }}>
        <DynamicTable
          headers={headers}
          data={tableData}
          loading={loading}
          height={"60vh"}
          page={page}
          rowsPerPage={rowsPerPage}
          totalRows={totalRecords > 0 ? totalRecords : ledgerData.length}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Box>
    </Box>
  );
};

export default StockLedger;
